import React, { Suspense } from 'react';
import { HashRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import Login from './pages/Login';
import Signup from './pages/Signup';
import ForgotPassword from './pages/ForgotPassword';
import OAuthCallback from './pages/OAuthCallback';
import LearnPage from './pages/LearnPage';
import ResetPassword from './pages/ResetPassword';
import {
  PrivacyPage,
  TermsPage,
  DisclaimerPage,
  BillingPolicyPage,
  ContactPage,
} from './pages/public/LegalPages';
import { DealProvider } from './context/DealContext';
import { ThemeModeProvider } from './theme/ThemeModeProvider';
import authService from './services/auth';

const Dashboard = React.lazy(() => import('./pages/protected/Dashboard'));
const NewDeal = React.lazy(() => import('./pages/protected/NewDeal'));
const DealWorkspace = React.lazy(() => import('./pages/protected/DealWorkspace'));
const Profile = React.lazy(() => import('./pages/protected/Profile'));
const ToolsHubPage = React.lazy(() => import('./pages/protected/tools/ToolsHubPage'));
const DealAnalyzerPage = React.lazy(() => import('./pages/protected/tools/DealAnalyzerPage'));
const CompFinderPage = React.lazy(() => import('./pages/protected/tools/CompFinderPage'));
const RiskToolPage = React.lazy(() => import('./pages/protected/tools/RiskToolPage'));
const ReviewToolPage = React.lazy(() => import('./pages/protected/tools/ReviewToolPage'));
const RentalAnalyzerPage = React.lazy(() => import('./pages/protected/tools/RentalAnalyzerPage'));
const BrrrrAnalyzerPage = React.lazy(() => import('./pages/protected/tools/BrrrrAnalyzerPage'));
const AdminOpsPage = React.lazy(() => import('./pages/protected/AdminOpsPage'));

const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const token = authService.getToken();
  if (!token) {
    return <Navigate to="/login" />;
  }
  return <>{children}</>;
};

const pageFallback = (
  <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
    <CircularProgress aria-label="Loading" />
  </Box>
);

const App: React.FC = () => {
  return (
    <Router>
      <ThemeModeProvider>
        <DealProvider>
          <Suspense fallback={pageFallback}>
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route path="/signup" element={<Signup />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/oauth/callback" element={<OAuthCallback />} />
              <Route path="/learn" element={<LearnPage />} />
              <Route path="/privacy" element={<PrivacyPage />} />
              <Route path="/terms" element={<TermsPage />} />
              <Route path="/disclaimer" element={<DisclaimerPage />} />
              <Route path="/billing-policy" element={<BillingPolicyPage />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/reset-password" element={<ResetPassword />} />

              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute>
                    <Dashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/profile"
                element={
                  <ProtectedRoute>
                    <Profile />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/deals/new"
                element={
                  <ProtectedRoute>
                    <NewDeal />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/deals/:dealId"
                element={
                  <ProtectedRoute>
                    <DealWorkspace />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/deals/:dealId/:section"
                element={
                  <ProtectedRoute>
                    <DealWorkspace />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/tools"
                element={
                  <ProtectedRoute>
                    <ToolsHubPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/tools/analyzer"
                element={
                  <ProtectedRoute>
                    <DealAnalyzerPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/tools/comps"
                element={
                  <ProtectedRoute>
                    <CompFinderPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/tools/risk"
                element={
                  <ProtectedRoute>
                    <RiskToolPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/tools/rental-analyzer"
                element={
                  <ProtectedRoute>
                    <RentalAnalyzerPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/tools/brrrr-analyzer"
                element={
                  <ProtectedRoute>
                    <BrrrrAnalyzerPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/tools/review"
                element={
                  <ProtectedRoute>
                    <ReviewToolPage />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/admin"
                element={
                  <ProtectedRoute>
                    <AdminOpsPage />
                  </ProtectedRoute>
                }
              />

              <Route path="/calculator" element={<Navigate to="/deals/new" replace />} />
              <Route path="/rehab-calculator" element={<Navigate to="/deals/new" replace />} />
              <Route path="/mortgage-calculator" element={<Navigate to="/deals/new" replace />} />
              <Route path="/market-analysis" element={<Navigate to="/deals/new" replace />} />

              <Route path="/" element={<Login />} />
            </Routes>
          </Suspense>
        </DealProvider>
      </ThemeModeProvider>
    </Router>
  );
};

export default App;
